// Considered: StatusFactorPanel — rejected because StatusFactorPanel is coupled to Atlas
// popover UX (PrimeReact Dropdown, popover chrome, no configure link). See spec Reuse Audit.
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { StatusValue } from 'types/spherePoints';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import type { FactorData } from '../Atlas/StatusPopover/StatusFactorPanel';
import { VELOCITY_BUCKET_LABEL_KEYS } from '../StatusCheckInDialog/checkInConstants';
import ManualFactorSourceSection from './ManualFactorSourceSection';
import PlaceholderFactorSourceSection from './PlaceholderFactorSourceSection';
import RollupSummarySection from './RollupSummarySection';
import type { RollupSummarySectionProps } from './RollupSummarySection';
import { useRollupExplanation } from './useRollupExplanation';
import './FactorSummaryMolecule.scss';

export interface FactorSummaryMoleculeProps {
  factor: FactorData;
  sphereId: string;
  hasEditPermission: boolean;
  /** Factor lifecycle mode (active | observing | paused) */
  mode?: string;
  /**
   * EAINT-842 Board 4 parity: forwarded to ManualFactorSourceSection so
   * the Latest check-in card can render a "next check-in due <when>"
   * line. Optional — the parent needs a schedule resolver or a `dueAt`
   * value on the factor to populate it.
   */
  nextDueAt?: string;
  onCheckInSubmitted?: () => void;
  onOpenConfigure?: () => void;
  /** Rollup factors only: the stored set and lookups that let the why-sentence close with the set's definition. */
  rollupSet?: NonNullable<RollupSummarySectionProps['rollupSet']>;
  readsAsContext?: NonNullable<RollupSummarySectionProps['readsAsContext']>;
}

const FactorSummaryMolecule: React.FC<FactorSummaryMoleculeProps> = ({
  factor,
  sphereId,
  hasEditPermission,
  mode,
  nextDueAt,
  onCheckInSubmitted,
  onOpenConfigure,
  rollupSet,
  readsAsContext,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();

  const isRollup = factor.factorType === 'related_spheres';
  const explanation = useRollupExplanation(isRollup ? sphereId : null, isRollup ? factor.id : null);
  const explanationSettled = explanation.data !== null || explanation.error !== null;

  // Fire once: on mount for every type but a rollup, which waits for its explanation to settle so the event can name the branch.
  const viewLogged = useRef(false);
  useEffect(() => {
    if (viewLogged.current || (isRollup && !explanationSettled)) return;
    viewLogged.current = true;
    logAnalyticsEvent('factor_summary_viewed', undefined, {
      sphereId,
      factorId: factor.id,
      factorType: factor.factorType,
      currentStatus: factor.status,
      velocityBucket: factor.velocityBucket,
      mode,
      ...(isRollup && {
        whyBranch: explanation.data?.explanation?.branch,
        hasHidden: (explanation.data?.explanation?.counts.hidden ?? 0) > 0,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explanationSettled]);

  const velocityKey = factor.velocityBucket
    ? VELOCITY_BUCKET_LABEL_KEYS[factor.velocityBucket]
    : null;

  const displayStatus = (factor.currentStatus ?? factor.status) as StatusValue;

  /*
   * EAINT-916: The RESULT row + optional broken-schedule badge are the
   * "why is it this color?" content the parent owns. For manual factors
   * this passes as a `resultSlot` prop into `ManualFactorSourceSection`
   * so it renders INSIDE the top card alongside the latest-check-in
   * narrative + quote — matching the handoff's two-panel layout (top
   * card: result + latest; bottom card: check-in history). Non-manual
   * factor types still render the block as their own sibling above the
   * placeholder source section.
   */
  const resultBlock = (
    <>
      <div className="factor-summary-molecule__result" data-testid="factor-summary-result">
        <span className="factor-summary-molecule__result-label">
          {t('statusFactors.summary.resultLabel')}
        </span>
        <span
          data-testid="factor-summary-status-chip"
          data-status={displayStatus}
          data-variant="chip"
        >
          <StatusBadgeAtom status={displayStatus} variant="chip" />
        </span>
        {velocityKey && (
          <span
            className="factor-summary-molecule__velocity"
            data-testid="factor-summary-velocity"
          >
            {t('statusFactors.summary.velocityLabel')} {t(velocityKey)}
          </span>
        )}
      </div>

      {factor.scheduleBroken && (
        <div
          className="factor-summary-molecule__schedule-broken"
          role="alert"
          data-testid="factor-schedule-broken"
        >
          <i className="pi pi-exclamation-triangle" aria-hidden="true" />
          <div className="factor-summary-molecule__schedule-broken-body">
            <div className="factor-summary-molecule__schedule-broken-title">
              {t('statusFactors.scheduleBroken.title', 'Schedule is broken')}
            </div>
            <div className="factor-summary-molecule__schedule-broken-detail">
              {t(
                `statusFactors.scheduleBroken.reason.${factor.scheduleBroken.code}`,
                factor.scheduleBroken.message,
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  let source: React.ReactNode;
  if (factor.factorType === 'manual') {
    source = (
      <ManualFactorSourceSection
        factorId={factor.id}
        sphereId={sphereId}
        hasEditPermission={hasEditPermission}
        mode={mode}
        nextDueAt={nextDueAt}
        onCheckInSubmitted={onCheckInSubmitted}
        resultSlot={resultBlock}
      />
    );
  } else if (isRollup) {
    source = (
      <RollupSummarySection
        explanation={explanation}
        resultBlock={resultBlock}
        statusLabel={factor.statusLabel ?? String(factor.status)}
        onOpenConfigure={onOpenConfigure}
        rollupSet={rollupSet}
        readsAsContext={readsAsContext}
      />
    );
  } else {
    source = (
      <>
        {/* Non-manual factor types keep the pre-EAINT-916 layout: the
            RESULT block renders as its own section above the
            placeholder source. Only manual factors get the two-card
            handoff-matching arrangement (which requires knowledge of
            the latest check-in — placeholder types don't have one). */}
        <div className="factor-summary-molecule__why">{resultBlock}</div>
        <div className="factor-summary-molecule__source">
          <PlaceholderFactorSourceSection onOpenConfigure={onOpenConfigure} />
        </div>
      </>
    );
  }

  return (
    <div className="factor-summary-molecule">
      {source}
    </div>
  );
};

export default FactorSummaryMolecule;
